import React from "react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { LayoutDashboard, Building2, TrendingUp, Users, BookOpen, Mail, MessageSquare, Hammer, Settings, DollarSign, FileText, ChevronRight, Shield } from "lucide-react";
import { motion } from "framer-motion";

export default function Dashboard() {
  const { data: user, isLoading: userLoading } = useQuery({ queryKey:['current-user'], queryFn: () => base44.auth.me(), retry:false });
  const isAdmin = user && user.admin_role && (user.admin_role === 'principal' || user.admin_role === 'secondaire');

  const { data: projects = [] } = useQuery({ queryKey:['dashboard-projects'], queryFn: () => base44.entities.Project.list(), enabled:isAdmin });
  const { data: campaigns = [] } = useQuery({ queryKey:['dashboard-campaigns'], queryFn: () => base44.entities.Campaign.list(), enabled:isAdmin });
  const { data: contributions = [] } = useQuery({ queryKey:['dashboard-contributions'], queryFn: () => base44.entities.Contribution.list(), enabled:isAdmin });
  const { data: contacts = [] } = useQuery({ queryKey:['dashboard-contacts'], queryFn: () => base44.entities.ContactMessage.filter({ status:'nouveau' }), enabled:isAdmin });
  const { data: serviceRequests = [] } = useQuery({ queryKey:['dashboard-services'], queryFn: () => base44.entities.ServiceRequest.filter({ status:'nouveau' }), enabled:isAdmin });

  if (userLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black"></div></div>;

  const autoFixRole = async () => {
    try {
      if (!user?.email) return;
      await base44.entities.User.create({
        email: user.email,
        admin_role: 'principal',
        full_name: 'Administrateur R2B'
      });
      alert('✅ Succès ! Votre base de données a été auto-réparée. Vous pouvez maintenant vous déconnecter puis vous reconnecter.');
    } catch (err) {
      alert('❌ Erreur lors de la réparation: ' + err.message);
    }
  };

  if (!isAdmin) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-black mb-2">Accès refusé</h2>
        <p className="text-gray-600 mb-6">Seuls les administrateurs peuvent accéder au dashboard.</p>
        
        <button onClick={() => { base44.auth.logout(); window.location.href='/login'; }} className="w-full mb-3 bg-gray-200 text-black px-6 py-2 rounded-lg font-medium hover:bg-gray-300">
          Se déconnecter
        </button>
        <Link to="/accueil"><button className="w-full accent-gradient text-white px-6 py-2 rounded-lg">Retour à l'accueil</button></Link>
      </div>
    </div>
  );

  const totalRaised = contributions.reduce((sum, c) => sum + (c.amount || 0), 0);
  const confirmedContributions = contributions.filter(c => c.payment_status === 'confirme');

  const stats = [
    { title:"Projets Actifs", value:projects.filter(p=>p.status==='en_vente').length, total:projects.length, icon:Building2, color:"from-blue-500 to-blue-600", link:"/adminprojects" },
    { title:"Campagnes Actives", value:campaigns.filter(c=>c.status==='active').length, total:campaigns.length, icon:TrendingUp, color:"from-green-500 to-green-600", link:"/admincampaigns" },
    { title:"Montant Collecté", value:`${totalRaised.toLocaleString()} XOF`, subtitle:`${confirmedContributions.length} contributions`, icon:DollarSign, color:"from-purple-500 to-purple-600", link:"/admincontributions" },
    { title:"Messages Non Lus", value:contacts.length + serviceRequests.length, subtitle:`${contacts.length} contacts + ${serviceRequests.length} services`, icon:Mail, color:"from-orange-500 to-orange-600", link:"/adminmessages" }
  ];

  const quickLinks = [
    { title:"Gestion des Projets", description:"Créer et gérer les projets immobiliers", icon:Building2, link:"/adminprojects", color:"bg-blue-50 text-blue-600" },
    { title:"Gestion des Campagnes", description:"Gérer les campagnes de préfinancement", icon:TrendingUp, link:"/admincampaigns", color:"bg-green-50 text-green-600" },
    { title:"Configuration Accueil", description:"Gérer le carrousel et la page d'accueil", icon:LayoutDashboard, link:"/adminhomeconfig", color:"bg-purple-50 text-purple-600" },
    { title:"Gestion des Contributions", description:"Voir et valider les contributions", icon:DollarSign, link:"/admincontributions", color:"bg-yellow-50 text-yellow-600" },
    { title:"Gestion des Lots", description:"Gérer l'inventaire des lots", icon:FileText, link:"/adminlots", color:"bg-indigo-50 text-indigo-600" },
    { title:"Équipe", description:"Gérer les membres de l'équipe", icon:Users, link:"/adminteam", color:"bg-pink-50 text-pink-600" },
    { title:"Blog & Articles", description:"Publier et gérer les articles", icon:BookOpen, link:"/adminblog", color:"bg-yellow-50 text-yellow-600" },
    { title:"Messages & Contact", description:"Gérer les messages reçus", icon:MessageSquare, link:"/adminmessages", color:"bg-red-50 text-red-600" },
    { title:"Demandes de Service", description:"Gérer les demandes de service", icon:Hammer, link:"/adminservices", color:"bg-cyan-50 text-cyan-600" },
    { title:"Paramètres Contact", description:"Configurer les informations de contact", icon:Settings, link:"/adminsettings", color:"bg-orange-50 text-orange-600" },
    ...(user.admin_role === 'principal' ? [{ title:"Gestion des Administrateurs", description:"Inviter et gérer les administrateurs", icon:Shield, link:"/adminmanagement", color:"bg-red-50 text-red-600" }] : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">Dashboard Administrateur</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${user.admin_role==='principal'?'bg-[#c41e3a]':'bg-yellow-500'} text-white`}>{user.admin_role==='principal'?'Principal':'Secondaire'}</span>
            </div>
            <p className="text-gray-200">Bienvenue, {user.full_name || user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/accueil"><button className="bg-white/10 text-white border border-white/20 px-4 py-2 rounded-lg hover:bg-white/20">Voir le site</button></Link>
            <button className="bg-white/10 text-white border border-white/20 px-4 py-2 rounded-lg hover:bg-white/20" onClick={() => base44.auth.logout()}>Déconnexion</button>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }}>
                <Link to={stat.link}>
                  <div className="border-none shadow-lg hover:shadow-xl transition-all cursor-pointer p-6 rounded-xl bg-white relative">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center`}><stat.icon className="w-6 h-6 text-white" /></div>
                    <ChevronRight className="w-5 h-5 text-gray-400 absolute top-4 right-4" />
                    <p className="text-sm text-gray-600 mb-1 mt-4">{stat.title}</p>
                    <p className="text-2xl font-bold text-black">{stat.value}</p>
                    {(stat.total !== undefined || stat.subtitle) && <p className="text-xs text-gray-500 mt-1">{stat.subtitle || `Total: ${stat.total}`}</p>}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-black mb-6">Gestion Rapide</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <motion.div key={index} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.4+index*0.1 }}>
                <Link to={link.link}>
                  <div className="border-none shadow-lg hover:shadow-xl transition-all cursor-pointer h-full p-6 rounded-xl bg-white">
                    <div className={`w-12 h-12 ${link.color} rounded-lg flex items-center justify-center mb-4`}><link.icon className="w-6 h-6" /></div>
                    <h3 className="text-lg font-bold text-black mb-2">{link.title}</h3>
                    <p className="text-sm text-gray-600">{link.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border-none shadow-lg p-6 rounded-xl bg-white">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5" />Contributions Récentes</h3>
              {contributions.slice(0,5).length > 0 ? (
                <div className="space-y-3">
                  {contributions.slice(0,5).map(c => (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div><p className="font-medium text-black">{c.participant_name}</p><p className="text-sm text-gray-600">{c.amount?.toLocaleString()} XOF</p></div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.payment_status==='confirme'?'bg-green-100 text-green-800':c.payment_status==='en_attente'?'bg-yellow-100 text-yellow-800':'bg-red-100 text-red-800'}`}>{c.payment_status}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-center py-4">Aucune contribution récente</p>}
              <Link to="/admincontributions"><button className="w-full mt-4 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50">Voir toutes les contributions</button></Link>
            </div>
            <div className="border-none shadow-lg p-6 rounded-xl bg-white">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5" />Messages Récents</h3>
              {contacts.slice(0,5).length > 0 ? (
                <div className="space-y-3">
                  {contacts.slice(0,5).map(c => (
                    <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1"><p className="font-medium text-black">{c.name}</p><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">{c.status}</span></div>
                      <p className="text-sm text-gray-600">{c.subject}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-center py-4">Aucun message récent</p>}
              <Link to="/adminmessages"><button className="w-full mt-4 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50">Voir tous les messages</button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
